import React, { useEffect, useState, useMemo } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import {
    Users, Search, X, ExternalLink, Pencil, Save, Loader2,
    CheckCircle2, AlertTriangle, Linkedin, MessageSquare,
    Award, Briefcase, Filter,
} from "lucide-react";

/**
 * /contacts — Rolodex view over the Ledger's people-shaped entries.
 *
 * Reads /api/contacts which deduplicates LinkedIn-sourced rows
 * (connections + DMs + endorsements + recommendations) by profile URL
 * or normalised name, then folds in any canon-clean `contact_correction`
 * ledger entries the operator has filed.
 *
 * Manual backfills POST to /api/contacts/correct which writes a new
 * ledger row — originals untouched (Canon Rule 1).
 */

const SORTS = [
    { value: "activity",     label: "Activity" },
    { value: "name",         label: "Name" },
    { value: "endorsements", label: "Endorsements" },
    { value: "recent_dm",    label: "Last DM" },
];

const EDITABLE_FIELDS = [
    { key: "name",         label: "Name" },
    { key: "company",      label: "Company" },
    { key: "position",     label: "Position" },
    { key: "profile_url",  label: "Profile URL" },
    { key: "email",        label: "Email" },
    { key: "connected_on", label: "Connected on" },
];

export default function Contacts() {
    const [items, setItems] = useState([]);
    const [totals, setTotals] = useState(null);
    const [count, setCount] = useState(0);
    const [totalFiltered, setTotalFiltered] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("activity");
    const [filter, setFilter] = useState("all");   // all | missing | endorsed | dm
    const [open, setOpen] = useState(null);        // selected contact dedup_key

    const PAGE_SIZE = 200;

    // iter 26.7 — paginated load. Previously: one 880KB blob that 4G phones
    // timed out fetching, and that production proxies cut mid-transfer.
    // Now: 200 rows ≈ 60KB per page, "Load more" pulls subsequent pages.
    const load = async (append = false, nextOffset = 0) => {
        if (append) setLoadingMore(true); else setLoading(true);
        setErr("");
        try {
            const { data } = await api.get("/contacts", {
                params: {
                    sort,
                    q: q.trim() || undefined,
                    limit: PAGE_SIZE,
                    offset: nextOffset,
                },
            });
            setItems((prev) => append ? [...prev, ...(data.items || [])] : (data.items || []));
            setTotals(data.totals || null);
            setCount((data.items || []).length + (append ? items.length : 0));
            setTotalFiltered(data.total_filtered || 0);
            setHasMore(!!data.has_more);
        } catch (e) {
            const status = e.response?.status;
            const detail = e.response?.data?.detail || e.message || "Load failed";
            setErr(`[HTTP ${status || "?"}] ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
        } finally {
            if (append) setLoadingMore(false); else setLoading(false);
        }
    };
    useEffect(() => { load(false, 0); /* eslint-disable-next-line */ }, [sort]);

    const loadMore = () => load(true, items.length);

    const onSearchSubmit = (e) => { e.preventDefault(); load(false, 0); };

    const filtered = useMemo(() => {
        if (filter === "all") return items;
        if (filter === "missing") return items.filter((c) => (c.missing_fields || []).length > 0);
        if (filter === "endorsed") return items.filter((c) => c.endorsement_count > 0);
        if (filter === "dm") return items.filter((c) => c.dm_count > 0);
        return items;
    }, [items, filter]);

    const selected = useMemo(
        () => items.find((c) => c.dedup_key === open) || null,
        [items, open],
    );

    return (
        <Shell>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Personnel · Derived Rolodex</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                        <Users className="w-10 h-10 text-stamp-red" strokeWidth={1.5} />
                        Contacts<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        Deduplicated from every people-shaped row in the Ledger — connections,
                        DMs, endorsements, recommendations. Originals untouched (Canon Rule 1);
                        corrections file as new dated entries.
                    </p>
                </div>
                <div className="pencil text-lg transform -rotate-2">
                    "Everyone you've ever known, on one ticket." — No. 2
                </div>
            </div>

            {/* Totals strip */}
            {totals && (
                <div className="paper-card p-4 mb-5 grid grid-cols-2 md:grid-cols-6 gap-3 text-center" data-testid="contacts-totals">
                    <Stat k="Distinct"      v={totals.distinct} />
                    <Stat k="With URL"      v={totals.with_url} />
                    <Stat k="With Company"  v={totals.with_company} />
                    <Stat k="With Position" v={totals.with_position} />
                    <Stat k="Endorsers"     v={totals.endorsers} />
                    <Stat k="DM partners"   v={totals.dm_partners} />
                </div>
            )}

            {/* Toolbar */}
            <div className="paper-card p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="contacts-toolbar">
                <form onSubmit={onSearchSubmit} className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <Search className="w-4 h-4 text-ink-soft shrink-0" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name, company, position, skill…"
                        className="flex-1 px-2 py-1.5 text-sm font-body bg-transparent border-b border-ink/20 focus:border-stamp-red outline-none"
                        data-testid="contacts-search"
                    />
                    <button
                        type="submit"
                        className="border border-ink/40 px-3 py-1 font-heading uppercase text-[10px] tracking-[0.2em] hover:border-stamp-red hover:text-stamp-red"
                        data-testid="contacts-search-submit"
                    >
                        Find
                    </button>
                </form>
                <div className="flex items-center gap-2">
                    <Filter className="w-3.5 h-3.5 text-ink-soft" />
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="bg-transparent border border-ink/30 px-2 py-1 font-heading uppercase text-[10px] tracking-[0.15em]"
                        data-testid="contacts-filter"
                    >
                        <option value="all">All</option>
                        <option value="missing">Missing fields</option>
                        <option value="endorsed">Endorsed</option>
                        <option value="dm">DM partners</option>
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <label className="label-typed text-[10px]">Sort</label>
                    <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="bg-transparent border border-ink/30 px-2 py-1 font-heading uppercase text-[10px] tracking-[0.15em]"
                        data-testid="contacts-sort"
                    >
                        {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>
            </div>

            {err && (
                <div className="paper-card border-2 border-stamp-red p-4 mb-4 flex items-start gap-3" data-testid="contacts-error">
                    <AlertTriangle className="w-4 h-4 text-stamp-red shrink-0 mt-0.5" />
                    <pre className="font-body text-xs whitespace-pre-wrap flex-1">{err}</pre>
                </div>
            )}

            {/* List */}
            <div className="paper-card p-2" data-testid="contacts-list">
                {loading ? (
                    <div className="p-6 text-center text-ink-faint font-body text-sm flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Aggregating ledger…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-6 text-center text-ink-faint font-body text-sm">
                        No contacts match the current filter.
                    </div>
                ) : (
                    <>
                        <div className="label-typed text-[9px] px-3 pt-2 pb-1 text-ink-faint">
                            Showing {filtered.length.toLocaleString()} of {totalFiltered.toLocaleString()}
                            {totalFiltered !== count && ` (${count.toLocaleString()} loaded)`}
                        </div>
                        <ul className="divide-y divide-ink/10" data-testid="contacts-rows">
                            {filtered.map((c) => (
                                <ContactRow
                                    key={c.dedup_key}
                                    c={c}
                                    onOpen={() => setOpen(c.dedup_key)}
                                />
                            ))}
                        </ul>
                        {hasMore && (
                            <div className="text-center py-4 border-t border-ink/10">
                                <button
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    data-testid="contacts-load-more"
                                    className="inline-flex items-center gap-2 border border-ink/40 hover:border-stamp-red hover:text-stamp-red px-4 py-2 font-heading uppercase text-[11px] tracking-[0.22em] disabled:opacity-50"
                                >
                                    {loadingMore ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                    {loadingMore ? "Loading…" : `Load ${Math.min(PAGE_SIZE, totalFiltered - count)} more`}
                                </button>
                                <div className="label-typed text-[9px] mt-2 text-ink-faint">
                                    {(totalFiltered - count).toLocaleString()} contacts remaining
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {selected && (
                <ContactDrawer
                    contact={selected}
                    onClose={() => setOpen(null)}
                    onSaved={() => { setOpen(null); load(false, 0); }}
                />
            )}
        </Shell>
    );
}

function Stat({ k, v }) {
    return (
        <div>
            <div className="font-heading text-2xl text-ink">{(v ?? 0).toLocaleString()}</div>
            <div className="label-typed text-[9px] text-ink-soft">{k}</div>
        </div>
    );
}

function ContactRow({ c, onOpen }) {
    return (
        <li
            className="px-3 py-2.5 cursor-pointer hover:bg-paper-dark/30 grid grid-cols-12 gap-2 items-center"
            onClick={onOpen}
            data-testid={`contact-row-${c.dedup_key}`}
        >
            <div className="col-span-12 md:col-span-5 min-w-0">
                <div className="font-heading text-base text-ink truncate flex items-center gap-2">
                    {c.name || <span className="text-ink-faint italic">[no name on file]</span>}
                    {c.recommendation_received && (
                        <span className="stamp-red text-[8px] px-1 border border-stamp-red flex-shrink-0">REC</span>
                    )}
                </div>
                <div className="label-typed text-[9px] text-ink-soft truncate">
                    {c.position && <span>{c.position}</span>}
                    {c.position && c.company && <span> · </span>}
                    {c.company && <span>{c.company}</span>}
                    {!c.position && !c.company && <span className="italic text-ink-faint">no role on file</span>}
                </div>
            </div>
            <div className="col-span-6 md:col-span-3 text-xs text-ink-soft truncate font-body">
                {c.endorsed_skills?.slice(0, 2).map((s) => s.skill).join(" · ")}
            </div>
            <div className="col-span-4 md:col-span-3 flex items-center gap-3 text-xs label-typed">
                {c.endorsement_count > 0 && (
                    <span className="flex items-center gap-1" title="Endorsements">
                        <Award className="w-3 h-3" /> {c.endorsement_count}
                    </span>
                )}
                {c.dm_count > 0 && (
                    <span className="flex items-center gap-1" title="DMs">
                        <MessageSquare className="w-3 h-3" /> {c.dm_count}
                    </span>
                )}
                {c.connected_on && (
                    <span className="flex items-center gap-1 hidden sm:flex" title="Connected">
                        <Briefcase className="w-3 h-3" /> {c.connected_on}
                    </span>
                )}
            </div>
            <div className="col-span-2 md:col-span-1 text-right">
                {(c.missing_fields || []).length > 0 && (
                    <span
                        className="text-stamp-red text-[9px] font-heading tracking-[0.15em]"
                        title={`Missing: ${c.missing_fields.join(", ")}`}
                        data-testid={`contact-missing-${c.dedup_key}`}
                    >
                        {c.missing_fields.length} ●
                    </span>
                )}
            </div>
        </li>
    );
}

function ContactDrawer({ contact, onClose, onSaved }) {
    const [edit, setEdit] = useState(false);
    const [form, setForm] = useState(() => ({
        name: contact.name || "",
        company: contact.company || "",
        position: contact.position || "",
        profile_url: contact.profile_url || "",
        email: contact.email || "",
        connected_on: contact.connected_on || "",
        note: "",
    }));
    const [evidence, setEvidence] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/contacts/${encodeURIComponent(contact.dedup_key)}`);
                if (!cancelled) setEvidence(data.evidence || []);
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [contact.dedup_key]);

    const save = async () => {
        setBusy(true);
        setErr("");
        try {
            // Only send fields that differ from the current contact value
            // (or are non-empty additions) — keeps the correction row lean
            const patch = { target_key: contact.dedup_key };
            for (const f of EDITABLE_FIELDS) {
                const cur = (contact[f.key] || "");
                const next = (form[f.key] || "").trim();
                if (next && next !== cur) patch[f.key] = next;
            }
            if (form.note.trim()) patch.note = form.note.trim();
            if (Object.keys(patch).length === 1) {
                setErr("No changes to file.");
                setBusy(false);
                return;
            }
            await api.post("/contacts/correct", patch);
            setSavedAt(new Date().toISOString());
            setEdit(false);
            setTimeout(onSaved, 600);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 z-50 flex items-stretch justify-end"
            onClick={onClose}
            data-testid="contact-drawer"
        >
            <div
                className="bg-paper w-full max-w-xl h-full overflow-y-auto p-6 border-l-2 border-ink/30"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-2xl text-ink uppercase tracking-[0.04em] truncate">
                        {contact.name || "[no name]"}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-ink-soft hover:text-stamp-red"
                        data-testid="contact-drawer-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {contact.profile_url && (
                    <a
                        href={contact.profile_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-stamp-blue text-xs font-body hover:underline mb-3"
                    >
                        <Linkedin className="w-3 h-3" /> {contact.profile_url}
                        <ExternalLink className="w-3 h-3" />
                    </a>
                )}

                {/* Field grid */}
                <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                        <div className="label-typed tw-prefix">Fields on file</div>
                        {!edit ? (
                            <button
                                onClick={() => setEdit(true)}
                                className="inline-flex items-center gap-1 border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-2 py-1 font-heading uppercase text-[10px] tracking-[0.2em]"
                                data-testid="contact-edit"
                            >
                                <Pencil className="w-3 h-3" /> Backfill
                            </button>
                        ) : (
                            <button
                                onClick={save}
                                disabled={busy}
                                className="inline-flex items-center gap-1 border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper-light px-2 py-1 font-heading uppercase text-[10px] tracking-[0.2em] disabled:opacity-40"
                                data-testid="contact-save"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                File correction
                            </button>
                        )}
                    </div>
                    <div className="space-y-2">
                        {EDITABLE_FIELDS.map((f) => (
                            <div key={f.key} className="grid grid-cols-3 gap-2 items-start">
                                <div className="label-typed text-[10px] pt-1">{f.label}</div>
                                {edit ? (
                                    <input
                                        type="text"
                                        value={form[f.key]}
                                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                        className="col-span-2 px-2 py-1 text-sm font-body bg-transparent border-b border-ink/30 focus:border-stamp-red outline-none"
                                        data-testid={`contact-input-${f.key}`}
                                    />
                                ) : (
                                    <div className="col-span-2 font-body text-sm text-ink min-w-0 break-words">
                                        {contact[f.key] || (
                                            <span className="text-ink-faint italic">— missing</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                        {edit && (
                            <div className="grid grid-cols-3 gap-2 items-start">
                                <div className="label-typed text-[10px] pt-1">Note (optional)</div>
                                <textarea
                                    rows={2}
                                    value={form.note}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                    className="col-span-2 px-2 py-1 text-sm font-body bg-transparent border border-ink/20 focus:border-stamp-red outline-none"
                                    data-testid="contact-input-note"
                                />
                            </div>
                        )}
                    </div>
                    {err && (
                        <div className="mt-3 text-stamp-red text-xs font-body flex items-center gap-2">
                            <AlertTriangle className="w-3 h-3" /> {err}
                        </div>
                    )}
                    {savedAt && (
                        <div className="mt-3 text-stamp-green text-xs font-body flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3" /> Correction filed at {savedAt.slice(11, 19)} UTC
                        </div>
                    )}
                </div>

                {/* Endorsement breakdown */}
                {contact.endorsed_skills?.length > 0 && (
                    <div className="mb-5">
                        <div className="label-typed tw-prefix mb-2">
                            Endorsed skills · {contact.endorsement_count}
                        </div>
                        <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm font-body">
                            {contact.endorsed_skills.map((s) => (
                                <li key={s.skill} className="flex items-baseline justify-between border-b border-ink/10 pb-0.5">
                                    <span className="text-ink truncate">{s.skill}</span>
                                    <span className="label-typed text-[9px]">×{s.count}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Evidence trail */}
                <div>
                    <div className="label-typed tw-prefix mb-2">
                        Evidence trail · {evidence.length}
                    </div>
                    {evidence.length === 0 ? (
                        <div className="text-ink-faint label-typed italic text-xs">Loading…</div>
                    ) : (
                        <ul className="space-y-2">
                            {evidence.map((e) => (
                                <li key={e.entry_id} className="border-b border-ink/10 pb-2 last:border-0">
                                    <div className="font-body text-xs text-ink truncate">{e.summary}</div>
                                    <div className="label-typed text-[9px] mt-0.5">
                                        {e.timestamp?.slice(0, 16).replace("T", " ")} · {e.kind}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
